import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Card, Container, Form, Pagination, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import ScopeIndicator from '../components/ScopeIndicator';
import { useAuth } from '../hooks/useAuth';
import { timelineAPI } from '../utils/api';
import { formatTimeInNurseryTimezone, getTodayDateInNurseryTimezone } from '../utils/timezone';

type ChildDay = {
  child: string;
  classroom: string;
  check_in: string | null;
  check_out: string | null;
  status: string;
};

const formatTime = (isoOrSql: string | null, nurseryTimezone: string = 'UTC') => {
  if (!isoOrSql) return '-';
  return formatTimeInNurseryTimezone(isoOrSql, nurseryTimezone);
};

const getDayMinute = (isoOrSql: string | null, nurseryTimezone: string = 'UTC') => {
  if (!isoOrSql) return null;

  const formatted = new Intl.DateTimeFormat('en-GB', {
    timeZone: nurseryTimezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(isoOrSql));

  const [hours, minutes] = formatted.split(':').map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return hours * 60 + minutes;
};

const getMarkerPercent = (isoOrSql: string | null, nurseryTimezone: string = 'UTC') => {
  const minute = getDayMinute(isoOrSql, nurseryTimezone);
  if (minute === null) return null;
  return Math.min(100, Math.max(0, (minute / 1439) * 100));
};

const ChildTimeline: React.FC = () => {
  const { t } = useTranslation('pages', { keyPrefix: 'childTimeline' });
  const { t: tc } = useTranslation('common');
  const { currentNursery } = useAuth();
  const navigate = useNavigate();

  const nurseryTimezone = currentNursery?.timezone || 'UTC';
  const [date, setDate] = useState<string>(() => getTodayDateInNurseryTimezone(nurseryTimezone));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ChildDay[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(50);

  const timeTicks = useMemo(() => ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00', '23:59'], []);

  const load = useCallback(async () => {
    if (!currentNursery?.nursery_id) return;

    setLoading(true);
    setError(null);
    try {
      const res = await timelineAPI.getTimeline(currentNursery.nursery_id, date);
      setData(res.attendanceData || []);
      setCurrentPage(1);
    } catch (e: any) {
      setError(e?.response?.data?.message || t('loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [currentNursery?.nursery_id, date, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (currentNursery) {
      setDate(getTodayDateInNurseryTimezone(currentNursery.timezone || 'UTC'));
      setCurrentPage(1);
    }
  }, [currentNursery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [recordsPerPage]);

  const totalPages = Math.ceil(data.length / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const currentData = data.slice(startIndex, endIndex);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleChildClick = useCallback((childName: string, classroomName: string) => {
    if (!currentNursery?.nursery_id) return;

    navigate(`/nursery/${currentNursery.nursery_id}/reports?view=individual&child_name=${encodeURIComponent(childName)}&classroom_name=${encodeURIComponent(classroomName)}&from_date=${date}&to_date=${date}`);
  }, [currentNursery?.nursery_id, date, navigate]);

  const renderPaginationItems = useCallback(() => {
    const items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item key={i} active={i === currentPage} onClick={() => handlePageChange(i)}>
          {i}
        </Pagination.Item>,
      );
    }
    return items;
  }, [currentPage, totalPages, handlePageChange]);

  return (
    <Layout title={t('title')}>
      <Container fluid className="px-3">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
          <h2 className="fw-bold text-primary mb-3 mb-md-0">{t('title')}</h2>
          <input
            type="date"
            className="form-control"
            value={date}
            onChange={(event) => setDate(event.target.value)}
            max={getTodayDateInNurseryTimezone(nurseryTimezone)}
            style={{ maxWidth: 180 }}
          />
        </div>

        <ScopeIndicator resource="childTimeline" />

        <Alert variant="info" className="mb-4">
          <strong>{t('showingFor')}</strong> {currentNursery?.description} {t('idLabel')} {currentNursery?.nursery_id})
        </Alert>

        {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

        {loading && (
          <div className="d-flex align-items-center mb-3">
            <Spinner className="me-2" animation="border" size="sm" />
            <span className="text-muted">{t('loading')}</span>
          </div>
        )}

        {!loading && data.length === 0 && (
          <Card className="text-center py-5">
            <Card.Body>
              <h5 className="text-muted mb-2">{t('noDataTitle')}</h5>
              <p className="text-muted mb-0">{t('noRecordsForDate', { date })}</p>
            </Card.Body>
          </Card>
        )}

        {!loading && currentData.map((childDay) => {
          const isVacation = childDay.status === 'vacation';
          const isHoliday = childDay.status === 'Holiday' || childDay.status === 'holiday';
          const isNotScheduled = childDay.status === 'Not Scheduled' || childDay.status === 'not_scheduled';
          const dropoffPercent = getMarkerPercent(childDay.check_in, nurseryTimezone);
          const pickupPercent = getMarkerPercent(childDay.check_out, nurseryTimezone);
          const hasRange = dropoffPercent !== null && pickupPercent !== null;

          return (
            <Card key={`${childDay.child}-${childDay.classroom}`} className={`mb-3 w-100 shadow-sm ${isVacation ? 'border-primary bg-light' : ''} ${isHoliday ? 'border-success bg-light' : ''} ${isNotScheduled ? 'border-secondary bg-light' : ''}`}>
              <Card.Body className="p-3 p-md-4">
                <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between mb-3 gap-2">
                  <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-2 gap-sm-3">
                    <strong
                      className="text-break fs-6 fs-md-5"
                      style={{ maxWidth: '200px', cursor: 'pointer', textDecoration: 'underline', color: 'black' }}
                      onClick={() => handleChildClick(childDay.child, childDay.classroom)}
                      title={t('clickToView', { name: childDay.child })}
                    >
                      {childDay.child}
                    </strong>
                    <Badge bg="secondary" className="text-wrap fs-7 fs-md-6">{childDay.classroom}</Badge>
                  </div>

                  <div className="text-muted small text-center text-md-end">
                    {isVacation ? (
                      <span className="text-nowrap text-primary fw-semibold">{t('onVacation')}</span>
                    ) : isHoliday ? (
                      <span className="text-nowrap fw-semibold" style={{ color: '#009688' }}>{t('holidayDay')}</span>
                    ) : isNotScheduled ? (
                      <span className="text-nowrap text-secondary fw-semibold">{t('notScheduled', { defaultValue: 'Not Scheduled' })}</span>
                    ) : (
                      <span className="text-nowrap">
                        {t('dropoff', { defaultValue: 'Drop-off' })}: {formatTime(childDay.check_in, nurseryTimezone)} / {t('pickup', { defaultValue: 'Pick-up' })}: {formatTime(childDay.check_out, nurseryTimezone)}
                      </span>
                    )}
                  </div>
                </div>

                <div className="position-relative" style={{ height: 36, minHeight: 36 }}>
                  {isVacation ? (
                    <div className="bg-primary rounded-3 w-100 h-100 d-flex align-items-center justify-content-center">
                      <span className="text-white fw-semibold fs-6">{t('vacationDay')}</span>
                    </div>
                  ) : isHoliday ? (
                    <div className="rounded-3 w-100 h-100 d-flex align-items-center justify-content-center" style={{ background: 'linear-gradient(135deg, #009688 0%, #20c997 100%)' }}>
                      <span className="text-white fw-semibold fs-6">{t('holidayDay')}</span>
                    </div>
                  ) : isNotScheduled ? (
                    <div className="rounded-3 w-100 h-100 d-flex align-items-center justify-content-center bg-secondary bg-opacity-25">
                      <span className="text-secondary fw-semibold fs-6">{t('notScheduled', { defaultValue: 'Not Scheduled' })}</span>
                    </div>
                  ) : (
                    <div className="bg-success bg-opacity-25 rounded-3 w-100 h-100 position-relative">
                      {hasRange && (
                        <div
                          className="position-absolute top-50 translate-middle-y bg-success"
                          style={{
                            left: `${Math.min(dropoffPercent, pickupPercent)}%`,
                            width: `${Math.abs(pickupPercent - dropoffPercent)}%`,
                            height: 4,
                            opacity: 0.7,
                          }}
                        />
                      )}
                      {dropoffPercent !== null && (
                        <div
                          className="position-absolute top-50 translate-middle rounded-circle bg-success border border-3 border-white shadow"
                          title={`${t('dropoff', { defaultValue: 'Drop-off' })}: ${formatTime(childDay.check_in, nurseryTimezone)}`}
                          style={{ left: `${dropoffPercent}%`, width: 18, height: 18 }}
                        />
                      )}
                      {pickupPercent !== null && (
                        <div
                          className="position-absolute top-50 translate-middle rounded-circle bg-primary border border-3 border-white shadow"
                          title={`${t('pickup', { defaultValue: 'Pick-up' })}: ${formatTime(childDay.check_out, nurseryTimezone)}`}
                          style={{ left: `${pickupPercent}%`, width: 18, height: 18 }}
                        />
                      )}
                    </div>
                  )}
                </div>

                {!isVacation && !isHoliday && !isNotScheduled && (
                  <div className="d-flex justify-content-between text-muted small mt-2 overflow-hidden" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {timeTicks.map((tick) => (
                      <span key={tick} className="text-nowrap px-1">{tick}</span>
                    ))}
                  </div>
                )}
              </Card.Body>
            </Card>
          );
        })}

        {!loading && data.length > 0 && (
          <div className="d-flex justify-content-between align-items-center p-3 border-top">
            <div className="d-flex align-items-center gap-3">
              <span className="text-muted small">
                {tc('messages.showing', { from: startIndex + 1, to: Math.min(endIndex, data.length), total: data.length, entity: tc('labels.child') })}
              </span>
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted small">{t('showLabel')}</span>
                <Form.Select
                  size="sm"
                  value={recordsPerPage}
                  onChange={(event) => setRecordsPerPage(parseInt(event.target.value))}
                  style={{ width: 'auto', minWidth: '80px' }}
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                </Form.Select>
                <span className="text-muted small">{t('perPage')}</span>
              </div>
            </div>

            {totalPages > 1 && (
              <Pagination className="mb-0">
                <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                {renderPaginationItems()}
                <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
              </Pagination>
            )}
          </div>
        )}
      </Container>
    </Layout>
  );
};

export default ChildTimeline;
